import React, { useEffect } from 'react';
import * as yup from "yup";
import {
  useForm,
  useFieldArray,
  Controller,
} from 'react-hook-form';
import { SectionLabel } from 'components/common/SectionLabel';
import {
  ButtonsContainer,
  Spacer,
} from 'components/common/ButtonsContainer';
import { Button } from 'components/common/Button';
import {
  CloseIcon,
  DeleteIcon,
  PlusIcon,
} from 'assets/icons';
import {
  StyledButtonContainer,
  StyledContainer,
  StyledFormContainer,
} from 'components/common/Forms/styled';
import { Label } from 'components/common/Input/Label';
import { toastSuccess } from 'utils/toast';
import { InputBasic } from 'components/common/Input';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckboxInput } from 'components/common/CheckboxInput';
import {
  StyledLookbackPeriod,
  StyledPeriod,
  StyledPlaceholder,
  StyledYTD,
} from './styled';
import {
  DuplicateMetricsForm,
  Metric,
  MetricOrigin,
} from 'interfaces/metrics';
import {
  useGetMetricQuery,
  usePostDuplicateMetricsMutation,
} from 'store/services/metrics';
import { Tooltip } from 'components/common/Tooltip';
import { useNavigate } from 'react-router';

interface Props {
  selectedMetrics: Metric[];
  onClose: () => void;
  onSuccess?: () => void;
  openedFromMetric?: boolean;
}

export const DuplicateMetrics = ({
  selectedMetrics,
  onClose,
  onSuccess,
  openedFromMetric,
}: Props) => {
  const [duplicateMetrics, { isLoading }] = usePostDuplicateMetricsMutation();
  const navigate = useNavigate();
  const { data: metric, isLoading: isLoadingMetric } = useGetMetricQuery(selectedMetrics[0]?.id, {
    skip: selectedMetrics.length !== 1,
  });

  const schema = yup.object({
    name: yup.string()
      .nullable(),
    lookbackPeriods: yup.array()
      .of(
        yup.object()
          .shape({
            period: yup.number()
              .when('ytd', {
                is: (val: boolean) => !val,
                then: yup.number()
                  .min(1, 'Period is required')
                  .max(36, 'Max 36 months')
                  .transform((value) => (isNaN(value) ? undefined : value))
                  .nullable()
                  .required('Period is required'),
              })
              .nullable(),
            ytd: yup.boolean(),
          }),
      ),
  })
    .required();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = useForm<DuplicateMetricsForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      lookbackPeriods: [],
    },
  });

  const {
    fields: lookbackPeriods,
    remove,
    append,
  } = useFieldArray({
    control,
    name: 'lookbackPeriods',
  });

  const handleSaveAndClose = handleSubmit((data) => {
    const metricsIds = selectedMetrics.map((metric) => metric.id);
    const payload = {
      lookbacks: data.lookbackPeriods.map((lp) => ({
        period: lp.ytd ? null : Number(lp.period),
        ytd: lp.ytd,
      })),
      metricsIds,
      name: metricsIds.length > 1
        ? null
        : data.name || null,
    };

    return duplicateMetrics(payload)
      .unwrap()
      .then((result) => {
        toastSuccess('Metric(s) successfully duplicated.');

        if (openedFromMetric) {
          onClose();
          navigate(`/metrics/${result[0]}`);
        } else {
          onClose();
          onSuccess && onSuccess();
        }
      });
  });

  useEffect(() => {
    if (metric?.filters.lookback && !isLoadingMetric) {
      setValue('lookbackPeriods', [metric.filters.lookback]);
    }
  }, [metric, isLoadingMetric, setValue]);

  if (!selectedMetrics.length) return null;

  const isAnyManual = selectedMetrics.some((metric) => metric.origin === MetricOrigin.MANUAL);

  return (
    <StyledContainer>
      <ButtonsContainer alignTop>
        <SectionLabel marginBottom={24}>
          Duplicate Metric{selectedMetrics.length > 1 && 's'}
        </SectionLabel>
        <Spacer />
        <Button
          aria-label="Close"
          variant="icon"
          size="large"
          onClick={onClose}
        >
          <CloseIcon />
        </Button>
      </ButtonsContainer>
      <StyledFormContainer onSubmit={handleSaveAndClose}>
        <InputBasic
          tooltip={selectedMetrics.length > 1
            ? 'When duplicating multiple metrics, duplicates names will be auto-generated. Also, previous lookback periods won\'t be applied to new metrics.'
            : 'Metric name is optional. If you won\'t provide it, it will get auto-generated.'
          }
          labelText="Metric Name"
          placeholder={selectedMetrics.length === 1
            ? `${selectedMetrics[0].name}`
            : watch('lookbackPeriods').length
              ? `Metric names based on selected lookback periods. See below`
              : `ie. ${selectedMetrics[0].name} (1)`
          }
          {...register('name')}
          onBlur={(e) => {
            const fieldValue = e.target.value;

            if (fieldValue) {
              setValue('name', fieldValue.trim(), { shouldValidate: true });
            }
          }}
          error={errors.name?.message}
          disabled={selectedMetrics.length > 1}
        />
        <div>
          <Label
            name="lookbackPeriods"
            labelText="Lookback Periods"
            {...(isAnyManual && { tooltip: 'One or more of selected metrics are manual - lookback periods are unavailable directly on those metrics.' })}
          />
          {lookbackPeriods.map((lp, index) => {
            const periodError = errors.lookbackPeriods?.[index]?.period?.message as string | undefined;
            const ytdError = errors.lookbackPeriods?.[index]?.ytd?.message as string | undefined;
            const localPeriod = watch(`lookbackPeriods.${index}.period`);
            const localYTD = watch(`lookbackPeriods.${index}.ytd`);

            const placeholder = localYTD
              ? `ie. ${watch('name') || selectedMetrics[0].name} (YTD)`
              : localPeriod
                ? `ie. ${watch('name') || selectedMetrics[0].name} (L${localPeriod}M)`
                : `Lookback Period`;

            if (isAnyManual) return;

            return (
              <StyledLookbackPeriod key={`lp_${lp.id}`}>
                <StyledPlaceholder>
                  <InputBasic
                    labelText=""
                    condensed
                    name={'none'}
                    placeholder={placeholder}
                    disabled
                  />
                </StyledPlaceholder>
                <StyledPeriod>
                  <InputBasic
                    {...register(`lookbackPeriods.${index}.period`)}
                    labelText=""
                    type="number"
                    max={36}
                    min={1}
                    condensed
                    disabled={watch(`lookbackPeriods.${index}.ytd`)}
                    error={periodError}
                    autoSelect
                  />
                  <span>
                    month(s)
                  </span>
                </StyledPeriod>
                <StyledYTD>
                  <Controller
                    name={`lookbackPeriods.${index}.ytd`}
                    control={control}
                    render={({ field }) =>
                      <CheckboxInput
                        isOptional
                        {...field}
                        onChange={(e) => {
                          setValue(`lookbackPeriods.${index}.period`, null);
                          field.onChange(e);
                        }}
                        checkboxLabel="YTD"
                        defaultValue={getValues(`lookbackPeriods.${index}.ytd`)}
                        error={ytdError}
                      />
                    }
                  />
                </StyledYTD>
                <Button
                  type="button"
                  variant="icon"
                  onClick={() => remove(index)}
                >
                  <DeleteIcon />
                </Button>
              </StyledLookbackPeriod>
            );
          })}
        </div>
        <ButtonsContainer>
          <Button
            type="button"
            variant="borderless"
            onClick={() => append({
              period: 0,
              ytd: false,
            })}
            disabled={lookbackPeriods.length === 10 || isAnyManual}
          >
            <PlusIcon />
            Add lookback period
          </Button>
          {lookbackPeriods.length === 10 && (
            <Tooltip title="Maximum of 10 Lookback Periods can be created at once." />
          )}
        </ButtonsContainer>
      </StyledFormContainer>
      <StyledButtonContainer pushRight>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isLoading}
        >
          CANCEL
        </Button>
        <Button
          type="button"
          onClick={handleSaveAndClose}
          disabled={isLoading}
          isLoading={isLoading}
          minWidth={200}
        >
          DUPLICATE METRIC{selectedMetrics.length > 1 && 'S'}
        </Button>
      </StyledButtonContainer>
    </StyledContainer>
  );
};
